<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import useAsync from "~/hooks/use-async";
import AuthAPI from "~/services/auth";
import useUserStore from "~/stores/user";
import getErrorLocalePathMessage from "~/utils/locale";

const { t, te } = useI18n();
const route = useRoute();
const router = useRouter();
const formData = reactive({
  email: "",
});

const {
  loading: providerLoading,
  error: socialError,
  run: executeProviderLogin,
} = useAsync(AuthAPI.signinWithProvider, { onSuccess: onSocialAuthSuccess });

function onSocialAuthSuccess() {
  const userStore = useUserStore();
  const query = {
    email: userStore.user?.email,
    fullName: userStore.user?.fullName,
    countryName: userStore.user?.countryName,
  };

  // Currently the way to know if user is already logged in via social authentication
  //  is to check the country field which is set after sign_up
  // TODO: Improve this flow.
  if (!query.countryName)
    router.push({ path: "/auth/sign-up/local/", query });
  else router.push((route.query.redirectUrl as string) || "/");
}

function onSubmit() {
  router.push({
    path: "/auth/sign-up/local/",
    query: { email: formData.email },
  });
}
const errorMessage = computed(() => socialError.value && getErrorLocalePathMessage(socialError.value, { t, te }).message);
const breakpoints = useBreakpoints(breakpointsTailwind);
const lg = breakpoints.greaterOrEqual("lg");
const { canShownHeader } = inject("authLayoutData") as { canShownHeader: boolean };

useHead({ title: t("labels.sign_up") });
definePageMeta({ layout: "auth" });
</script>

<template>
  <AuthHeader mode="sign-in" :class="{ '!my-0': canShownHeader && !lg }" :transparent="!canShownHeader || lg" />

  <BaseMain class="min-h-screen  mb-[10rem] sm:mb-0">
    <div class="flex flex-col items-center justify-center h-full px-4 pt-[12rem] sm:pt-0">
      <div class="w-full max-w-[400px]">
        <h1 class="text-heading-1-bold">
          {{ t("labels.sign_up") }}
        </h1>
        <p class="mt-4 text-xs text-secondary">
          {{ t("pages.auth_sign_up.enter_your_details_below") }}
        </p>

        <BaseAlert
          v-if="errorMessage"
          type="danger"
          class="mt-6"
          :text="errorMessage"
        />

        <form class="mt-8" @submit.prevent="onSubmit()">
          <BaseInput
            v-model="formData.email"
            type="email"
            name="email"
            :label="t('labels.email')"
            autocomplete="username"
            pattern="[a-zA-Z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,4}$"
            size="30"
            required
          />

          <BaseButton
            :disabled="providerLoading"
            class="mt-16 font-bold"
            name="continue-signup"
            full-width
          >
            {{ t("labels.continue") }}
          </BaseButton>
        </form>

        <AuthProviderButtons
          :disabled="providerLoading"
          @click-provider="executeProviderLogin"
        />
      </div>
      <AuthTerms class="text-center" />
    </div>
  </BaseMain>
</template>
